import { Link } from 'react-router-dom';

import Card, { CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

import { usePageTitle } from '../hooks/shared';

const LAST_UPDATED = 'April 9, 2026';

const commitments = [
  {
    title: 'Accessible by default',
    body: 'We aim to make core voter tasks usable with a keyboard, screen reader, zoom, mobile device, and reduced-motion preferences.',
  },
  {
    title: 'Plain language',
    body: 'We try to explain voting deadlines, registration steps, ballot information, and delivery choices in direct, understandable language.',
  },
  {
    title: 'Inclusive design',
    body: 'We design for people with different disabilities, internet speeds, device sizes, and levels of civic familiarity.',
  },
  {
    title: 'Ongoing improvement',
    body: 'Accessibility is not a one-time checklist. We review issues, prioritize fixes, and keep improving the product over time.',
  },
];

const supportedAreas = [
  'Semantic headings, labels, landmarks, and link text where practical',
  'Keyboard access for navigation, forms, buttons, dialogs, and interactive controls',
  'Color contrast and non-color indicators for important states and actions',
  'Responsive layouts that work at larger zoom levels and on small screens',
  'Support for reduced motion and focus visibility',
  'Clear error messaging and form guidance where available',
];

const currentLimits = [
  'Some third-party or embedded content may not fully match the accessibility standard of the rest of the app.',
  'Maps, visual data displays, or election-source documents may sometimes require additional text alternatives or fallback views.',
  'Because election information can come from government or authority sources, some upstream content may arrive in formats that are harder to make fully accessible immediately.',
];

const helpItems = [
  'the page or feature you were using',
  'what assistive technology or browser you were using, if relevant',
  'what went wrong',
  'what you were trying to accomplish',
];

export default function Accessibility() {
  usePageTitle('Accessibility');

  return (
    <div className="bg-neutral-50 min-h-screen py-8 md:py-10">
      <div className="container-custom max-w-4xl">
        <nav className="flex items-center gap-1.5 text-sm text-neutral-500 mb-6">
          <Link to="/" className="hover:text-primary-600 transition-colors">
            Home
          </Link>
          <span>/</span>
          <span className="text-neutral-800">Accessibility</span>
        </nav>

        <section className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6 md:p-8 mb-5">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary-700 mb-3">
              Accessibility
            </p>
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-neutral-900 mb-4">
              Voting information should be usable by everyone.
            </h1>
            <p className="text-base md:text-lg text-neutral-700 leading-7 mb-4">
              This app is intended to help people find elections, understand deadlines, review
              ballot information, and access voter tools without unnecessary barriers. We are
              committed to building a product that is inclusive, accessible, and respectful of
              disabled voters and all users.
            </p>
            <p className="text-sm text-neutral-500">Last updated: {LAST_UPDATED}</p>
          </div>
        </section>

        <section className="grid gap-5 md:grid-cols-2 mb-5">
          {commitments.map((item) => (
            <Card key={item.title} className="rounded-2xl border-neutral-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-serif text-neutral-900">{item.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-neutral-700 leading-7">{item.body}</p>
              </CardContent>
            </Card>
          ))}
        </section>

        <section className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6 md:p-8 mb-5">
          <h2 className="text-xl font-serif font-bold text-neutral-900 mb-3">Our aim</h2>
          <p className="text-neutral-700 leading-7 mb-4">
            We aim to conform to WCAG 2.2 Level AA for the parts of the app we design and control.
            That includes trying to make content perceivable, operable, understandable, and robust
            across common browsers and assistive technologies.
          </p>
          <p className="text-neutral-700 leading-7">
            Accessibility also means fairness. Voting tools should not assume a perfect device,
            perfect vision, perfect dexterity, fast internet, or prior civic knowledge.
          </p>
        </section>

        <section className="grid gap-5 md:grid-cols-2 mb-5">
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6 md:p-8">
            <h2 className="text-xl font-serif font-bold text-neutral-900 mb-3">
              Areas we actively support
            </h2>
            <ul className="space-y-3 text-neutral-700 leading-7 list-disc pl-5">
              {supportedAreas.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6 md:p-8">
            <h2 className="text-xl font-serif font-bold text-neutral-900 mb-3">
              Known limitations
            </h2>
            <ul className="space-y-3 text-neutral-700 leading-7 list-disc pl-5">
              {currentLimits.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6 md:p-8 mb-5">
          <h2 className="text-xl font-serif font-bold text-neutral-900 mb-3">
            Need help or want to report a barrier?
          </h2>
          <p className="text-neutral-700 leading-7 mb-4">
            If you run into an accessibility issue, we want to know. We take good-faith reports
            seriously and will use them to improve the experience.
          </p>
          <p className="text-neutral-700 leading-7 mb-3">
            When you contact us, it helps to include:
          </p>
          <ul className="space-y-2 text-neutral-700 leading-7 list-disc pl-5 mb-5">
            {helpItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm text-neutral-800 leading-6">
              Add your real support channel here before shipping, such as a support email, contact
              form, or accessible help workflow.
            </p>
            <p className="text-sm text-neutral-600 mt-2">
              Example placeholder: <span className="font-medium">accessibility@yourdomain.com</span>
            </p>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6 md:p-8 mb-5">
          <h2 className="text-xl font-serif font-bold text-neutral-900 mb-3">
            Scope of this statement
          </h2>
          <p className="text-neutral-700 leading-7 mb-4">
            This statement applies to the public web application and the features we maintain
            directly. It may not fully cover third-party services, linked government websites,
            uploaded source documents, or external widgets that are outside our control.
          </p>
          <p className="text-neutral-700 leading-7">
            When we link to outside election authorities or public records, we still try to present
            information in a usable way inside this app whenever possible.
          </p>
        </section>

        <section className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6 md:p-8">
          <h2 className="text-xl font-serif font-bold text-neutral-900 mb-3">Continuous review</h2>
          <p className="text-neutral-700 leading-7 mb-4">
            We may update this page as the app changes, new accessibility issues are identified, or
            our practices improve.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/privacy"
              className="inline-flex items-center rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-100 transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="inline-flex items-center rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-100 transition-colors"
            >
              Terms of Service
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
